/**
 * Public Careers page — /careers
 *
 * Lists all active job openings from the backend. Each card opens a
 * modal with full details + an "Apply on WhatsApp" form. The form
 * (name, place, email, phone) is composed into a WhatsApp deep-link
 * pre-filled message and sent to +91 99956 56232.
 *
 * Deep link: `https://wa.me/919995656232?text=<url-encoded message>`
 *   - Opens WhatsApp on mobile or WhatsApp Web on desktop.
 *   - We `window.open(..., "_blank")` so the page stays put.
 */
import React, { useEffect, useMemo, useState } from "react";
import { MarketingNav, MarketingFooter, BTN_GREEN } from "./_shared";
import SEO from "../../lib/seo";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import {
  Briefcase, MapPin, Clock, Buildings, Trophy, Sparkle, WhatsappLogo, X,
  ArrowRight,
} from "@phosphor-icons/react";

const CAREERS_WHATSAPP_NUMBER = "919995656232"; // +91 99956 56232 (no + in wa.me links)

const TYPE_LABEL = {
  full_time: "Full-time",
  part_time: "Part-time",
  internship: "Internship",
  contract: "Contract",
  freelance: "Freelance",
};
const LEVEL_LABEL = {
  entry: "Entry level",
  mid: "Mid level",
  senior: "Senior",
  lead: "Lead",
  any: "Any level",
};

export default function Careers() {
  const [jobs, setJobs] = useState([]);
  const [openJob, setOpenJob] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/public/careers")
      .then(r => setJobs(r.data || []))
      .catch(e => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  const grouped = useMemo(() => {
    const m = {};
    for (const j of jobs) (m[j.department] ??= []).push(j);
    return m;
  }, [jobs]);

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="careers-page">
      <SEO title="Careers · Rescigo Health"
            description="Build the future of biomedical waste management in India. Join Rescigo — field operations, product engineering, government liaison, healthcare and CSR roles across Kerala."
            path="/careers"
            keywords="Rescigo careers, biomedical waste jobs Kerala, LSGD liaison job Kerala, waste management jobs Trivandrum, healthcare civic-tech jobs"/>
      <MarketingNav/>

      <section className="pt-40 pb-16 border-b border-slate-100">
        <div className="max-w-5xl mx-auto px-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-[10px] uppercase tracking-[0.25em] font-semibold mb-6">
            <Sparkle size={11} weight="fill"/> We're hiring across Kerala
          </div>
          <h1 className="font-heading text-5xl sm:text-6xl leading-[1.02] tracking-tight text-slate-900">
            Build what your grandkids<br/>will breathe in.
          </h1>
          <p className="text-lg text-slate-600 mt-6 max-w-2xl">
            Rescigo is one of Kerala's fastest-growing civic-tech companies. We collect, treat and
            digitise the toughest waste streams — biomedical, diaper, sanitary. Our mission needs
            operators, engineers, doctors, policy folks and CSR partners.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Stat label="Kerala footprint" value="13 cities"/>
            <Stat label="LSGDs served" value="26"/>
            <Stat label="Waste diverted YTD" value="214 tonnes"/>
            <Stat label="Team" value="42 · growing"/>
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-5xl mx-auto px-6">
          {loading && <div className="text-slate-500 text-sm">Loading roles…</div>}
          {!loading && jobs.length === 0 && (
            <div className="text-center py-16">
              <Briefcase size={40} weight="duotone" color="#94a3b8" className="mx-auto"/>
              <div className="mt-4 text-slate-600">No open roles at the moment.</div>
              <div className="text-xs text-slate-400 mt-1">
                Drop us a note anyway — <a href="mailto:care@rescigo.in" className="underline">care@rescigo.in</a>
              </div>
            </div>
          )}
          {!loading && Object.entries(grouped).map(([dept, list]) => (
            <div key={dept} className="mb-14" data-testid={`careers-dept-${dept}`}>
              <div className="flex items-baseline justify-between border-b border-slate-200 pb-3 mb-5">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">{dept}</div>
                  <div className="text-lg font-heading mt-0.5">{list.length} open role{list.length !== 1 && "s"}</div>
                </div>
              </div>
              <div className="divide-y divide-slate-100">
                {list.map(j => (
                  <button key={j.id}
                          onClick={() => setOpenJob(j)}
                          data-testid={`careers-open-${j.id}`}
                          className="w-full text-left py-6 grid grid-cols-1 md:grid-cols-12 gap-4 items-center hover:bg-slate-50 transition group">
                    <div className="md:col-span-6">
                      <div className="text-lg font-heading text-slate-900 group-hover:text-emerald-700">{j.title}</div>
                      {j.summary && <div className="text-sm text-slate-600 mt-1">{j.summary}</div>}
                    </div>
                    <div className="md:col-span-2 text-xs text-slate-600 flex items-center gap-1"><MapPin size={11}/> {j.location}</div>
                    <div className="md:col-span-2 text-xs text-slate-600 flex items-center gap-1"><Clock size={11}/> {TYPE_LABEL[j.employment_type] || j.employment_type}</div>
                    <div className="md:col-span-2 text-right text-emerald-700 text-sm font-medium inline-flex items-center gap-1 justify-end">
                      View & apply <ArrowRight size={13} weight="bold"/>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {openJob && (
        <JobModal job={openJob} onClose={() => setOpenJob(null)}/>
      )}

      <MarketingFooter/>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
function Stat({ label, value }) {
  return (
    <div className="px-4 py-3 rounded-lg border border-slate-200 bg-slate-50/60">
      <div className="text-[9px] uppercase tracking-[0.22em] text-slate-500">{label}</div>
      <div className="text-base font-medium text-slate-900 mt-0.5">{value}</div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
function JobModal({ job, onClose }) {
  const [form, setForm] = useState({ name: "", place: "", email: "", phone: "" });
  const [showApply, setShowApply] = useState(false);

  const applyOnWhatsApp = () => {
    if (!form.name.trim() || !form.place.trim() || !form.phone.trim()) {
      toast.error("Please fill in name, place and phone at minimum.");
      return;
    }
    const msg = [
      "Hi Rescigo, I'd like to apply for a role.",
      "",
      `*Position*: ${job.title} — ${job.location}`,
      `*Employment type*: ${TYPE_LABEL[job.employment_type] || job.employment_type}`,
      "",
      `*Name*: ${form.name}`,
      `*Place*: ${form.place}`,
      `*Email*: ${form.email || "—"}`,
      `*Phone*: ${form.phone}`,
      "",
      "Please share the next steps. Thanks!",
    ].join("\n");
    const url = `https://wa.me/${CAREERS_WHATSAPP_NUMBER}?text=${encodeURIComponent(msg)}`;
    window.open(url, "_blank", "noopener");
    toast.success("WhatsApp opened — please tap Send to submit your application.");
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto"
         onClick={onClose}
         data-testid="job-modal">
      <div className="bg-white rounded-2xl w-full max-w-3xl my-8 relative"
           onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose}
                data-testid="job-modal-close"
                className="absolute top-4 right-4 p-2 rounded-full hover:bg-slate-100">
          <X size={18}/>
        </button>

        <div className="p-8 border-b border-slate-100">
          <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">
            {job.department}
          </div>
          <h2 className="font-heading text-3xl mt-2 leading-tight">{job.title}</h2>
          <div className="mt-4 flex flex-wrap gap-3 text-xs">
            <Chip Icon={MapPin} text={job.location}/>
            <Chip Icon={Clock} text={TYPE_LABEL[job.employment_type] || job.employment_type}/>
            <Chip Icon={Trophy} text={LEVEL_LABEL[job.experience_level] || job.experience_level}/>
            {job.compensation_range && <Chip Icon={Buildings} text={job.compensation_range}/>}
          </div>
        </div>

        <div className="p-8 space-y-6">
          {job.summary && <p className="text-slate-700 text-base">{job.summary}</p>}
          {job.description && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-2">About the role</div>
              <p className="text-slate-700 text-sm whitespace-pre-line leading-relaxed">{job.description}</p>
            </div>
          )}
          {(job.responsibilities || []).length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-2">Responsibilities</div>
              <ul className="text-slate-700 text-sm space-y-1.5 list-disc list-inside">
                {job.responsibilities.map((r, i) => <li key={i}>{r}</li>)}
              </ul>
            </div>
          )}
          {(job.requirements || []).length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-2">What we're looking for</div>
              <ul className="text-slate-700 text-sm space-y-1.5 list-disc list-inside">
                {job.requirements.map((r, i) => <li key={i}>{r}</li>)}
              </ul>
            </div>
          )}
        </div>

        <div className="p-8 border-t border-slate-100 bg-slate-50/50 rounded-b-2xl">
          {!showApply ? (
            <button onClick={() => setShowApply(true)}
                    data-testid="job-cta-apply"
                    className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-[#25D366] hover:bg-[#20b957] text-white text-sm font-semibold transition">
              <WhatsappLogo size={15} weight="fill"/> Apply on WhatsApp
            </button>
          ) : (
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-3">Your details</div>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Full name" required value={form.name}
                       testid="apply-name"
                       onChange={v => setForm(f => ({ ...f, name: v }))}/>
                <Field label="Place / City" required value={form.place}
                       testid="apply-place"
                       onChange={v => setForm(f => ({ ...f, place: v }))}/>
                <Field label="Email (optional)" value={form.email} type="email"
                       testid="apply-email"
                       onChange={v => setForm(f => ({ ...f, email: v }))}/>
                <Field label="Phone / WhatsApp number" required value={form.phone} type="tel"
                       testid="apply-phone"
                       onChange={v => setForm(f => ({ ...f, phone: v }))}/>
              </div>
              <div className="mt-5 flex flex-wrap items-center gap-3">
                <button onClick={applyOnWhatsApp}
                        data-testid="apply-submit-whatsapp"
                        className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-[#25D366] hover:bg-[#20b957] text-white text-sm font-semibold">
                  <WhatsappLogo size={15} weight="fill"/> Send application on WhatsApp
                </button>
                <button onClick={() => setShowApply(false)}
                        className="h-12 px-4 rounded-full border border-slate-300 text-sm text-slate-700 hover:bg-slate-100">
                  Cancel
                </button>
                <div className="text-[11px] text-slate-500 max-w-xs">
                  We'll open WhatsApp with a pre-filled message to our HR at
                  <span className="font-mono"> +91 99956 56232</span>. Tap send to submit.
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Chip({ Icon, text }) {
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-100 text-slate-700">
      <Icon size={11}/> {text}
    </span>
  );
}

function Field({ label, value, onChange, required, type = "text", testid }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1">
        {label}{required && " *"}
      </span>
      <input type={type} value={value}
             onChange={e => onChange(e.target.value)}
             required={required}
             data-testid={testid}
             className="w-full h-11 px-3 border border-slate-300 rounded-md bg-white text-sm focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600 outline-none"/>
    </label>
  );
}
